import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { soapImprovementAPI } from '@/features/formatter/apis/soapImprovement'
import {
  SoapImprovementSuggestion,
  ApplySuggestionResponse,
} from '@/apps/latest/features/formatter/types/soapImprovement'
import { useToast } from '@/shared/contexts/ToastContext'

// ========================================
// Constants
// ========================================

const DEFAULT_POLL_INTERVAL = 2000 // 2秒
const DEFAULT_MAX_POLLS = 2 // 最大2回

// ========================================
// useSoapImprovementSuggestionsQuery
// ========================================

interface UseSoapImprovementSuggestionsQueryOptions {
  pollingEnabled?: boolean
  maxPolls?: number
  pollInterval?: number
}

interface UseSoapImprovementSuggestionsQueryReturn {
  suggestions: SoapImprovementSuggestion[]
  isLoading: boolean
  isPolling: boolean
  error: Error | null
  refetch: () => void
}

/**
 * SOAP改善提案取得クエリ（TanStack Query + ポーリング）
 *
 * 責務:
 * - SOAP改善提案の取得
 * - オプションでポーリング（固定間隔2秒、最大2回）
 *
 * 使用例:
 * ```tsx
 * const { suggestions, isLoading } = useSoapImprovementSuggestionsQuery('yakureki-id')
 * ```
 */
export const useSoapImprovementSuggestionsQuery = (
  yakurekiId: string | undefined,
  options: UseSoapImprovementSuggestionsQueryOptions = {}
): UseSoapImprovementSuggestionsQueryReturn => {
  const {
    pollingEnabled = true,
    maxPolls = DEFAULT_MAX_POLLS,
    pollInterval = DEFAULT_POLL_INTERVAL,
  } = options

  const query = useQuery({
    queryKey: queryKeys.soapImprovement.detail(yakurekiId ?? ''),
    queryFn: async (): Promise<SoapImprovementSuggestion[]> => {
      if (!yakurekiId) return []
      return await soapImprovementAPI.getSuggestions(yakurekiId)
    },
    enabled: !!yakurekiId,
    refetchInterval: pollingEnabled
      ? (query) => {
          // データ取得できたら停止
          const data = query.state.data
          if (data && data.length > 0) {
            return false
          }

          // 最大ポーリング回数に達したら停止
          if (query.state.dataUpdateCount >= maxPolls) {
            return false
          }

          return pollInterval
        }
      : false,
    staleTime: 0, // ポーリング中は常に新鮮なデータを取得
  })

  // 提案の並び替え（固定順序：優先度、カテゴリ、作成日時順）
  const sortedSuggestions = [...(query.data ?? [])].sort((a, b) => {
    // 1. 優先度でソート (高い順)
    if (a.priority !== b.priority) {
      return b.priority - a.priority
    }

    // 2. カテゴリでソート
    const categoryOrder = { basic: 0, kasan: 1, followup: 2 }
    if (categoryOrder[a.category] !== categoryOrder[b.category]) {
      return categoryOrder[a.category] - categoryOrder[b.category]
    }

    // 3. 作成日時でソート (古い順で固定)
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  })

  return {
    suggestions: sortedSuggestions,
    isLoading: query.isLoading,
    isPolling: query.isFetching && !query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}

// ========================================
// useApplySuggestionMutation
// ========================================

/**
 * 改善提案適用Mutation（TanStack Query）
 *
 * 責務:
 * - 改善提案の適用
 * - 成功時にキャッシュinvalidate + Toast表示
 */
export const useApplySuggestionMutation = (yakurekiId: string | undefined) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (
      suggestionId: string
    ): Promise<ApplySuggestionResponse | null> => {
      if (!yakurekiId) return null
      return await soapImprovementAPI.applySuggestion(yakurekiId, suggestionId)
    },
    onSuccess: (result) => {
      if (result?.success) {
        // キャッシュをinvalidate
        queryClient.invalidateQueries({
          queryKey: queryKeys.soapImprovement.detail(yakurekiId ?? ''),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.yakureki.detail(yakurekiId ?? ''),
        })

        // AIの回答をToast表示
        const toastMessage = result.result?.aiResponse
          ? `改善提案を適用しました: ${result.result.aiResponse}`
          : '改善提案を適用しました'
        showToast(toastMessage, 'success', 5000)
      } else {
        showToast(result?.message || '手動での更新が必要です', 'info')
      }
    },
    onError: () => {
      showToast('改善提案の適用に失敗しました', 'error')
    },
  })
}

// ========================================
// useDismissSuggestionMutation
// ========================================

/**
 * 改善提案却下Mutation（TanStack Query）
 *
 * 責務:
 * - 改善提案の却下
 * - 成功時にキャッシュinvalidate + Toast表示
 */
export const useDismissSuggestionMutation = (
  yakurekiId: string | undefined
) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (suggestionId: string): Promise<boolean> => {
      if (!yakurekiId) return false
      const result = await soapImprovementAPI.dismissSuggestion(
        yakurekiId,
        suggestionId
      )
      if (!result.success) {
        throw new Error(result.error || '提案の却下に失敗しました')
      }
      return true
    },
    onSuccess: () => {
      // キャッシュをinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.soapImprovement.detail(yakurekiId ?? ''),
      })
      showToast('提案を却下しました', 'success')
    },
    onError: (error: Error) => {
      showToast(error.message || '提案の却下に失敗しました', 'error')
    },
  })
}
